import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Stub from './Stub'

// Same look as the kit's Word (ownerKit); imported directly from Stub so the
// ui barrel never loops back through a feature file.
function Word({ className = '', ...rest }) {
  return (
    <Stub
      variant="word"
      className={`text-sm leading-pitch disabled:text-ink-mute disabled:no-underline ${className}`}
      {...rest}
    />
  )
}

Word.propTypes = { className: PropTypes.string }

/**
 * A text action that asks inline before it runs, so no native browser dialog
 * is ever needed. First click swaps the trigger for a row:
 * `[question] [confirmLabel] [Keep it]`. Focus moves to the confirm button
 * when the row appears, Escape or "Keep it" puts the trigger back.
 *
 * Callers whose trigger is an icon button or a Stub pass `renderTrigger`
 * (`({ onClick, disabled }) => <Button ... />`) so the existing look stays.
 */
export default function ConfirmAction({
  question,
  confirmLabel = 'Yes',
  keepLabel = 'Keep it',
  onConfirm,
  disabled = false,
  children,
  renderTrigger,
  className = '',
  confirmClassName = '',
}) {
  const [asking, setAsking] = useState(false)
  const rowRef = useRef(null)

  useEffect(() => {
    if (asking) rowRef.current?.querySelector('button')?.focus()
  }, [asking])

  if (!asking) {
    const open = () => setAsking(true)
    return renderTrigger ? (
      renderTrigger({ onClick: open, disabled })
    ) : (
      <Word
        type="button"
        className={className}
        disabled={disabled}
        onClick={open}
      >
        {children}
      </Word>
    )
  }

  return (
    <span
      ref={rowRef}
      role="group"
      aria-label={question}
      className="inline-flex flex-wrap items-baseline gap-x-4 gap-y-1"
      onKeyDown={e => {
        if (e.key === 'Escape') {
          e.stopPropagation()
          setAsking(false)
        }
      }}
    >
      <span className="font-sans text-sm text-ink">{question}</span>
      <Word
        type="button"
        disabled={disabled}
        className={confirmClassName}
        onClick={() => {
          setAsking(false)
          onConfirm()
        }}
      >
        {confirmLabel}
      </Word>
      <Word type="button" onClick={() => setAsking(false)}>
        {keepLabel}
      </Word>
    </span>
  )
}

ConfirmAction.propTypes = {
  question: PropTypes.string.isRequired,
  confirmLabel: PropTypes.string,
  keepLabel: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  children: PropTypes.node,
  renderTrigger: PropTypes.func,
  className: PropTypes.string,
  confirmClassName: PropTypes.string,
}
